/**
 * Blocked Users Page — list of blocked users with unblock button.
 * Opens from settings drawer.
 */

import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowLeft, ShieldCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useBlockedUsers, useBlockUser } from '@turnifly/shared/hooks'
import { triggerHapticImpact as hapticImpact } from '@turnifly/shared/utils'

interface BlockedUsersPageProps {
  isOpen: boolean
  onClose: () => void
  userId: string
}

export const BlockedUsersPage: React.FC<BlockedUsersPageProps> = ({ isOpen, onClose, userId }) => {
  const { t } = useTranslation()
  const { data: blocked, isLoading } = useBlockedUsers(userId)
  const unblockMutation = useBlockUser()

  const handleUnblock = (blockedId: string): void => {
    hapticImpact('light')
    unblockMutation.mutate({ blockerId: userId, blockedId, isBlocked: true })
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 28, stiffness: 280 }}
          className="fixed inset-0 z-[60] bg-bg flex flex-col"
        >
          <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.06] tg-header-safe-area shrink-0">
            <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/[0.04] tap-highlight-transparent">
              <ArrowLeft size={20} className="text-text" />
            </button>
            <h2 className="font-display font-bold text-text">{t('profile.settings.blockedUsers')}</h2>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3">
            {isLoading ? (
              <div className="py-12 text-center text-text-hint text-sm">{t('common.loading')}</div>
            ) : !blocked?.length ? (
              <div className="py-12 text-center text-text-hint text-sm">{t('profile.noBlockedUsers')}</div>
            ) : (
              <div className="space-y-2">
                {blocked.map((u) => (
                  <div key={u.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/[0.04]">
                    <div className="w-10 h-10 rounded-full bg-white/[0.06] flex items-center justify-center shrink-0 overflow-hidden">
                      {u.avatar_url ? (
                        <img src={u.avatar_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-sm text-text-hint">{u.full_name.charAt(0)}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text truncate">{u.full_name}</p>
                      {u.username && <p className="text-xs text-text-hint">@{u.username}</p>}
                    </div>
                    <button
                      onClick={() => handleUnblock(u.id)}
                      disabled={unblockMutation.isPending}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.06] text-xs text-text-hint hover:bg-white/[0.06] transition-colors"
                    >
                      <ShieldCheck size={12} />
                      {t('profile.unblock')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
